import { CancellationToken } from "../Promises/CancellationTokenSource";
import { ITree } from "./ITree";

export interface IAsyncTree<T> extends ITree<T> {
    /**
     * Průchodem do hloubky navštíví všechny uzly.
     * @param visitor Funkce spouštěná nad každým uzlem.
     */
    traverseDFS(visitor: (data: T | null) => void): void;
    /**
     * Projde asynchronně všechny uzly stromu a to tím způsobem, že potomci se navštěvují až když je dokončeno navštívení rodičovského uzlu.
     * Metoda vrací, až když se dokončí návštěvy všech uzlů.
     * @param visitor Asynchronní funkce spouštěná nad každým uzlem.
     * @param traverseProcessDetails Objekt obsahující informace o průběhu průchodu stromem nebo průběžné výsledky.
     * @param processChildrenPrecondition Funkce určující, zda se má pokračovat v průchodu na potomky. Pokud není uvedená, tak se prochází všichni potomci.
     * @param failedPreconditionAction Funkce vyvolaná při nesplnění processChildrenPrecondition.
     * @param afterChildrenMethod Funkce vyvolaná po dokončení procházení všech potomků.
     * @param finallyMethod Funkce vyvolaná vždy po dokončení celého procházení.
     * @param ct Token umožňující stornování průchodu.
     */
    traverseRootsFinishFirstAsync<TVisitResult = IBaseAsyncTreeVisitorResult, TTraverseProcessDetails = ITraverseProcessDetails>(
        visitor: (data: T | null, node?: IAsyncTree<T>, traverseProcessDetails?: TTraverseProcessDetails) => Promise<TVisitResult>,
        traverseProcessDetails?: TTraverseProcessDetails,
        processChildrenPrecondition?: (visitResult: TVisitResult) => boolean,
        failedPreconditionAction?: (sth: TVisitResult) => boolean,
        afterChildrenMethod?: (element: T | null, childrenResult: IBaseAsyncTreeChildrenResult, visitorResult: TVisitResult) => TVisitResult,
        finallyMethod?: (data: T | null) => void,
        ct?: CancellationToken,
    ): Promise<TVisitResult>;
}

/**
 * Průběžná data o průchodu stromem, se kterými může visitor dále pracovat.
 * Může sloužit například pro předání informací o výsledku vyhodnocení rodičovského uzlu jeho potomkům.
 */
// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface ITraverseProcessDetails {
}

export interface IBaseAsyncTreeVisitorResult<TTraverseProcessDetails = ITraverseProcessDetails> {
    traverseDetails?: TTraverseProcessDetails;
    result?: any;
    cancel?: boolean;
}

export class IBaseAsyncTreeVisitorResult {
    private static _properties: (keyof IBaseAsyncTreeVisitorResult)[] = ["traverseDetails", "result", "cancel"];

    public static [Symbol.hasInstance](instance: any): boolean {
        // POZOR! Nestandardní definice. Protože má interface jen nepovinné property, nemůžeme je kontrolovat povinně. Aby to zase nebyla jen úplně obecná kontrola, jestli je to objekt bez dalších specifik,
        // tak se kontroluje, že pokud má nějaké property, tak by měl obsahovat alespoň některou z nepovinných. Pokud nemá žádné property, kontrolou projde.
        return instance != null && (Reflect.ownKeys(instance).length === 0 || this._properties.some(propertyKey => Reflect.has(instance, propertyKey)));
    }
}

export interface IBaseAsyncTreeChildrenResult {
    results: (IBaseAsyncTreeVisitorResult | void)[];
}

export class IBaseAsyncTreeChildrenResult {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && Reflect.has(instance, "results");
    }
}
